import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';

import type { NormalizedDateFilter } from '@/components/DatePicker/RichDatePicker';
import { useDateRangeFilter } from '@/hooks/useDateRangeFilter';
import { usePaginationSearch } from '@/lib/listPage';

import { VendorInvoiceDealTable } from './-components/VendorInvoiceDealTable';
import { VendorInvoiceFilterBar } from './-components/VendorInvoiceFilterBar';
import { useVendorInvoiceDealList } from './-query';

const ALL_STATUSES = [
  'ScheduledReconciliation',
  'Discrepancy',
  'CompletedReconciliation',
] as const;
const WITHOUT_RECONCILED = ['ScheduledReconciliation', 'Discrepancy'] as const;

const SearchSchema = z.object({
  pageIndex: z.number().default(0),
  pageSize: z.number().default(100),
  freeText: z.string().optional(),
  hideReconciled: z.boolean().default(false),
  billingPeriodFrom: z.string().optional(),
  billingPeriodTo: z.string().optional(),
  paymentDeadlineFrom: z.string().optional(),
  paymentDeadlineTo: z.string().optional(),
});

export const Route = createFileRoute(
  '/_staff/devices/_list/contract-and-cost/_contract-and-cost/deal/_deal/vendor-invoice/',
)({
  component: VendorInvoiceTab,
  validateSearch: zodValidator(SearchSchema),
});

function VendorInvoiceTab() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [pagination, setPagination] = usePaginationSearch({
    search,
    onSearchChange: (s) => navigate({ search: s }),
  });

  const statuses = useMemo(
    () => (search.hideReconciled ? [...WITHOUT_RECONCILED] : [...ALL_STATUSES]),
    [search.hideReconciled],
  );

  const { data, isPending } = useVendorInvoiceDealList({
    page: search.pageIndex + 1,
    pageSize: search.pageSize,
    freeText: search.freeText,
    statuses,
  });

  const {
    currentFilter: billingPeriodFilter,
    handleFilterChange: handleBillingPeriodQueryChange,
  } = useDateRangeFilter(search.billingPeriodFrom, search.billingPeriodTo);

  const handleBillingPeriodFilterChange = useCallback(
    ({ normalized }: { normalized?: NormalizedDateFilter }) => {
      const { fromDate, toDate } = handleBillingPeriodQueryChange(normalized);
      navigate({
        search: (prev) => ({
          ...prev,
          billingPeriodFrom: fromDate,
          billingPeriodTo: toDate,
          pageIndex: 0,
        }),
      });
    },
    [navigate, handleBillingPeriodQueryChange],
  );

  const {
    currentFilter: paymentDeadlineFilter,
    handleFilterChange: handlePaymentDeadlineQueryChange,
  } = useDateRangeFilter(search.paymentDeadlineFrom, search.paymentDeadlineTo);

  const handlePaymentDeadlineFilterChange = useCallback(
    ({ normalized }: { normalized?: NormalizedDateFilter }) => {
      const { fromDate, toDate } = handlePaymentDeadlineQueryChange(normalized);
      navigate({
        search: (prev) => ({
          ...prev,
          paymentDeadlineFrom: fromDate,
          paymentDeadlineTo: toDate,
          pageIndex: 0,
        }),
      });
    },
    [navigate, handlePaymentDeadlineQueryChange],
  );

  const handleHideReconciledChange = useCallback(
    (checked: boolean) => {
      navigate({
        search: (prev) => ({
          ...prev,
          hideReconciled: checked,
          pageIndex: 0,
        }),
      });
    },
    [navigate],
  );

  return (
    <>
      <VendorInvoiceFilterBar
        searchValue={search.freeText ?? ''}
        onSearchChange={(e) =>
          navigate({
            search: (prev) => ({
              ...prev,
              freeText: e.target.value || undefined,
              pageIndex: 0,
            }),
          })
        }
        billingPeriodFilter={billingPeriodFilter}
        onBillingPeriodFilterChange={handleBillingPeriodFilterChange}
        paymentDeadlineFilter={paymentDeadlineFilter}
        onPaymentDeadlineFilterChange={handlePaymentDeadlineFilterChange}
        hideReconciled={search.hideReconciled}
        onHideReconciledChange={handleHideReconciledChange}
        onExport={() => {}}
      />
      <VendorInvoiceDealTable
        data={data?.items ?? []}
        isPending={isPending}
        totalCount={data?.count}
        pageCount={data?.maxPage}
        pagination={pagination}
        onPaginationChange={setPagination}
        className="flex-1 min-h-0"
      />
    </>
  );
}
